import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import {AlertTitle} from '@chakra-ui/react';

import {AlertIcon} from '@amblique/king-living/app/components/icons';
import {ProductList as ProductListComponent} from '@amblique/king-living/app/components/product-list';
import {RangeDownloads} from '@amblique/king-living/app/components/range-downloads';
import {RANGE_PLP_PAGE_SIZE} from '@amblique/king-living/app/constants';
import {usePageContext} from '@amblique/king-living/app/hooks/contentful';
import {usePixleeScript} from '@amblique/king-living/app/hooks/use-pixlee-script';
import Alert from '@amblique/king-living/app/theme/components/base/alert';

/**
 * ProductListing component
 *
 * @param {Object} props - Component props
 * @param {Contentful.ProductListing} props.component - ProductListing component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement}
 */
export const ProductListing = ({component, isComplete = true, isFetching = false, ...props}) => {
  const {
    isEdit,
    isCategoryPage,

    productSearchResult,
    isProductSearchLoading,
    isProductSearchFetched,
    isProductSearchRefetching,
    category,
    categoryError,
  } = usePageContext();

  // Load Pixlee script for UGC widgets on product pages
  usePixleeScript();

  return (
    isCategoryPage ? (
      <>
        <ProductListComponent
          category={category}
          // PLP heading: Contentful-authored title; `LazyLoadGrid` falls back to `category.name`
          // when this is empty/whitespace so the page never renders headless.
          contentfulTitle={component?.title}
          error={categoryError}
          // KINDW-103 / FSD PLP-07 — Contentful curates which refinement attributes appear as
          // filter chips for this range. Site preference `c_rangePLPFilterTagsEnabled` is the
          // global kill-switch; its check lives in `LazyLoadGrid` so we can keep the rule of
          // "single owner of grid + filter state" in one place.
          filterTagAttributeIds={component?.filterTagAttributeIds}
          isFetched={isProductSearchFetched}
          isLoading={isProductSearchLoading}
          isRefetching={isProductSearchRefetching}
          pageSize={RANGE_PLP_PAGE_SIZE}
          productSearchResult={productSearchResult}
          // KINDW-99 / FSD PLP-03 — Range Detail PLP renders the lazy-load grid. The legacy
          // /category/* PLP keeps `lazyLoad` defaulted to false via its own page wrapper.
          lazyLoad
        />
        {/* KINDW-104 / FSD PLP-06 — Range Utilities downloads. Each CTA is conditional on
            its Contentful asset; nothing renders when both are unset. */}
        <RangeDownloads
          productCard={component?.productCard}
          assemblyManual={component?.assemblyManual}
        />
      </>
    ) : isEdit ? (
      <Alert status="warning">
        <AlertIcon />
        <AlertTitle>Product Listing - Dynamic</AlertTitle>

        <FormattedMessage
          defaultMessage="This component can only be added to '{aspectType}' pages."
          id="contentful.error.invalid_aspects"
          values={{aspectType: 'Product Listing'}}
        />
      </Alert>
    ) : null
  );
};

ProductListing.displayName = 'ProductListing';
ProductListing.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
